import React, { useState } from 'react';

import Utility from '../components/Utility';
import { getList } from '../services/utilitiesService';
import { UtilCommand } from '../Types/Util';

const Utilities = () => {
    const [active, setActive] = useState<string>('');
    const raw_data: unknown = getList().data;
    if (!raw_data) return <div>No data</div>;

    // Sort data by frequency: 'frequent' first, then 'normal', then 'seldom'
    const frequencyOrder: Record<string, number> = {
        frequent: 1,
        normal: 2,
        seldom: 3,
    };

    const data: UtilCommand[] = Object.values(raw_data).sort((a, b) => {
        const freqA = frequencyOrder[a.frequency || 'seldom'] || 3;
        const freqB = frequencyOrder[b.frequency || 'seldom'] || 3;
        return freqA - freqB;
    });

    const commandHandler = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        command: string,
    ): void => {
        event.preventDefault();
        setActive(command);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Utilities
                    </h1>
                    <p className="text-gray-600">
                        Manual intervention scripts for system administration
                    </p>
                </div>

                {/* Main Content */}
                <div className="flex flex-row gap-6">
                    {/* Left Sidebar - Command List */}
                    <div className="w-80 flex-shrink-0">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden sticky top-6">
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                    Available Scripts
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    {data.length} script{data.length !== 1 ? 's' : ''} available
                                </p>
                            </div>
                            <nav className="p-2 max-h-[calc(100vh-12rem)] overflow-y-auto">
                                {data.map((utilData, index) => {
                                    const isActive = active === utilData.name;

                                    // Determine text color based on frequency
                                    const getFrequencyTextColor = () => {
                                        switch (utilData.frequency) {
                                            case 'frequent':
                                                return 'text-green-700';
                                            case 'normal':
                                                return 'text-yellow-700';
                                            case 'seldom':
                                            default:
                                                return 'text-gray-700';
                                        }
                                    };

                                    return (
                                        <button
                                            key={utilData.name}
                                            onClick={(e) => commandHandler(e, utilData.name)}
                                            className={`
                                                w-full text-left px-4 py-3 rounded-lg mb-1
                                                transition-all duration-150 ease-in-out
                                                group relative
                                                ${
                                                    isActive
                                                        ? 'bg-blue-50 border border-blue-200 shadow-sm'
                                                        : 'hover:bg-gray-50 border border-transparent'
                                                }
                                            `}
                                        >
                                            {/* Active Indicator */}
                                            {isActive && (
                                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-full" />
                                            )}

                                            <div className="flex items-start gap-3 pl-2">
                                                {/* Icon */}
                                                <div
                                                    className={`
                                                        flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
                                                        transition-colors duration-150
                                                        ${
                                                            isActive
                                                                ? 'bg-blue-100 text-blue-600'
                                                                : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200 group-hover:text-gray-600'
                                                        }
                                                    `}
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div
                                                        className={`
                                                            text-sm font-medium truncate
                                                            ${
                                                                isActive
                                                                    ? 'text-blue-900'
                                                                    : `${getFrequencyTextColor()} group-hover:text-gray-900`
                                                            }
                                                        `}
                                                    >
                                                        {utilData.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                        {utilData.description.substring(0, 50)}
                                                        {utilData.description.length > 50 ? '...' : ''}
                                                    </div>
                                                </div>

                                                {/* Arrow indicator */}
                                                {isActive && (
                                                    <svg
                                                        className="w-4 h-4 text-blue-600 flex-shrink-0"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M9 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>
                    </div>

                    {/* Right Content - Utility Details */}
                    <div className="flex-1 min-w-0">
                        {active ? (
                            data.map((utilData) => {
                                return (
                                    <div
                                        key={utilData.name}
                                        className={active === utilData.name ? '' : 'hidden'}
                                    >
                                        <Utility data={utilData} />
                                    </div>
                                );
                            })
                        ) : (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                                <div className="text-center">
                                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <svg
                                            className="w-12 h-12 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Select a Script
                                    </h3>
                                    <p className="text-sm text-gray-500 max-w-sm mx-auto">
                                        Choose a utility script from the list on the left to view details and execute it
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Utilities;
